// ENGINE_RULES §2 — periods (REQ-021, REQ-261 AC3). Public module (ARCH §8.1): apps and the engine import
// it. All dates are calendar dates in the AD ACCOUNT's time zone; `today` is supplied by the caller (from
// `accountToday`, the only function here that consults a time zone — `calendarDaysBetween` counts whole days
// through it). Pure: no clock, no I/O (D-020).

import type { AlertWindow } from '../types/alerts';
import type { IsoDate, IsoInstant } from '../types/primitives';
import type { PeriodPreset } from '../types/settings';
import {
  addDays,
  compareIsoDates,
  daysInclusive,
  firstOfMonth,
  parseIsoDate,
  previousMonth,
  toEpochDay,
} from './isoDate';

export interface DateRange {
  since: IsoDate;
  until: IsoDate;
}

/** A preset resolved against the account's `today` — inclusive dates, its comparison period, and whether it
 *  is still filling up ("so far", REQ-261 AC3). Assignable to the contract's `ResolvedPeriod` (`meta.period`,
 *  API §12.1), which is the server's echo of the same four dates with the compare pair nullable. */
export interface ResolvedPreset extends DateRange {
  preset: PeriodPreset;
  /** The immediately preceding range of equal length (REQ-021 AC3) — never "the same days last month". */
  compareSince: IsoDate;
  compareUntil: IsoDate;
  /** Inclusive length in calendar days. */
  days: number;
  /** `until` is today: the period is partial and labelled "so far". */
  includesToday: boolean;
}

export type PeriodErrorCode = 'invalid_range';

/** Thrown by `resolvePeriod` for a custom range that is reversed, in the future, or malformed. The harness
 *  and the API both read `code`; the message is for developers, never for users (ARCH §2.7). */
export class PeriodError extends Error {
  readonly code: PeriodErrorCode;
  constructor(code: PeriodErrorCode, message: string) {
    super(message);
    this.name = 'PeriodError';
    this.code = code;
  }
}

// ---- today in the account's zone ---------------------------------------------------------------------------

const formatters = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    // en-US + numeric parts: the parts are read by type, so the locale's order and separators never matter.
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/**
 * The calendar date it is in the ad account's time zone at `now` (an ISO instant or epoch milliseconds — what
 * `Clock.now()` returns). The device's zone plays no part: at 05:30 UTC on 22 Sep an account in
 * `America/Chicago` is on the 22nd whether the phone is in Tokyo (14:30) or Los Angeles (22:30 on the 21st).
 * DST-safe by construction — `Intl` applies the zone's rules for that instant, so the 23- and 25-hour days
 * fall where they should (`dr-now-dst-*`).
 */
export function accountToday(now: IsoInstant | number, accountTimeZone: string): IsoDate {
  const ms = typeof now === 'number' ? now : new Date(now).getTime();
  if (!Number.isFinite(ms))
    throw new RangeError(`accountToday: "${String(now)}" is not an instant`);
  let year = 0;
  let month = 0;
  let day = 0;
  for (const part of dateFormatter(accountTimeZone).formatToParts(new Date(ms))) {
    if (part.type === 'year') year = Number(part.value);
    else if (part.type === 'month') month = Number(part.value);
    else if (part.type === 'day') day = Number(part.value);
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Whole CALENDAR days from `from` to `to` in `timeZone` — the day number of `to` there minus the day number of
 * `from` there: 0 when both fall on the same calendar day, 1 when `to` is the day after, negative when `to` is
 * earlier. Either instant is an ISO string or epoch milliseconds (what `Clock.now()` returns).
 *
 * **This is the one place a day count a person reads is computed, and it is never elapsed hours ÷ 24.** That
 * is D-870's rule (REQ-001 AC3) and the trap it wrote down before anyone fell into it: "2 days ago", "the oldest
 * has been waiting 2 days" and "fired within the last 7 days" are differences of calendar days in a NAMED zone,
 * so they cannot flip with the hour of the day, and a DST day of 23 or 25 hours is still one day. Counting
 * hours was the bug twice — `Math.floor(ms / 86_400_000)` read a change staged at 6:24 am two days back as
 * "a day" at a 12:30 am clock (BUG-203), and 168 elapsed hours kept a row in the inbox on the day its age
 * already read as a date (D-912). A surface that counts hours is the bug.
 *
 * WHICH zone is the caller's rule, not this function's: an alert's age ladder counts in the DEVICE's zone
 * (REQ-261 AC4 — `timeAgo`); the inbox's seven-day membership and a staged change's wait count in the ACCOUNT's
 * zone (ENGINE §10.7 — `calendarDaysSinceFire`; both apps' `wholeDaysSince`). A duration under six hours carries
 * no zone and is not counted here (D-870's exemption — `timeAgo` keeps those in hours).
 */
export function calendarDaysBetween(
  from: IsoInstant | number,
  to: IsoInstant | number,
  timeZone: string,
): number {
  return toEpochDay(accountToday(to, timeZone)) - toEpochDay(accountToday(from, timeZone));
}

// ---- presets ---------------------------------------------------------------------------------------------

/** The immediately preceding range of equal length: `n = until − since + 1`, previous = `[since − n, since − 1]`. */
export function previousPeriod(range: DateRange): DateRange {
  const n = daysInclusive(range.since, range.until);
  return { since: addDays(range.since, -n), until: addDays(range.since, -1) };
}

/** "Last N full days" — `[today − N, today − 1]`; full days are days before today (ENGINE §2). */
export function lastFullDays(today: IsoDate, n: number): DateRange {
  return { since: addDays(today, -n), until: addDays(today, -1) };
}

/**
 * A preset → concrete inclusive dates plus the comparison period, from the account's `today`.
 * `today` = [today, today] ("so far") · `yesterday` · `last_7d` / `last_30d` = the 7 / 30 full days before
 * today (REQ-021 AC2: today excluded) · `this_month` = [1st, today] · `last_month` = the previous calendar
 * month · `custom` = as given, `since ≤ until ≤ today`, else `PeriodError('invalid_range')` — never swapped.
 */
export function resolvePeriod(
  preset: PeriodPreset,
  today: IsoDate,
  custom?: Partial<DateRange> | null,
): ResolvedPreset {
  parseIsoDate(today);
  const range = presetRange(preset, today, custom);
  const previous = previousPeriod(range);
  return {
    preset,
    since: range.since,
    until: range.until,
    compareSince: previous.since,
    compareUntil: previous.until,
    days: daysInclusive(range.since, range.until),
    includesToday: range.until === today,
  };
}

function presetRange(
  preset: PeriodPreset,
  today: IsoDate,
  custom?: Partial<DateRange> | null,
): DateRange {
  switch (preset) {
    case 'today':
      return { since: today, until: today };
    case 'yesterday': {
      const yesterday = addDays(today, -1);
      return { since: yesterday, until: yesterday };
    }
    case 'last_7d':
      return lastFullDays(today, 7);
    case 'last_30d':
      return lastFullDays(today, 30);
    case 'this_month':
      return { since: firstOfMonth(today), until: today };
    case 'last_month':
      return previousMonth(today);
    case 'custom': {
      const since = custom?.since;
      const until = custom?.until;
      if (!since || !until)
        throw new PeriodError('invalid_range', 'a custom period needs since and until');
      try {
        parseIsoDate(since);
        parseIsoDate(until);
      } catch (e) {
        throw new PeriodError('invalid_range', (e as Error).message);
      }
      if (compareIsoDates(since, until) > 0)
        throw new PeriodError('invalid_range', `since ${since} is after until ${until}`);
      if (compareIsoDates(until, today) > 0)
        throw new PeriodError('invalid_range', `until ${until} is after today ${today}`);
      return { since, until };
    }
  }
}

// ---- alert windows -----------------------------------------------------------------------------------------

const WINDOW_DAYS: Record<Exclude<AlertWindow, 'today' | 'yesterday'>, number> = {
  last_3d: 3,
  last_7d: 7,
  last_14d: 14,
  last_30d: 30,
};

/** Alert windows (ENGINE §10) are built like the presets: `today` is partial, the rest are full days. */
export function resolveWindow(window: AlertWindow, today: IsoDate): DateRange {
  parseIsoDate(today);
  if (window === 'today') return { since: today, until: today };
  if (window === 'yesterday') return lastFullDays(today, 1);
  return lastFullDays(today, WINDOW_DAYS[window]);
}
